import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

const PROFILE_PHOTO = "/images/profile.png";

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] },
};

const About = () => {
  const { t } = useLanguage();

  return (
    <section
      id="rolam"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left – Text */}
          <motion.div {...reveal} data-testid="about-text">
            <span className="inline-block text-xs md:text-sm font-medium uppercase tracking-[0.25em] text-[#3B82F6] mb-6">
              {t.about.label}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1B2A4A] leading-[1.1] tracking-tight">
              {t.about.headingStart}{" "}
              <span className="italic font-medium">{t.about.headingItalic}</span>{" "}
              {t.about.headingEnd}
            </h2>
            <div className="mt-8 space-y-5 text-base md:text-lg text-[#475569] leading-relaxed font-light">
              {t.about.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div className="mt-10 flex items-center gap-6 pt-8 border-t border-[#1B2A4A]/10">
              <div>
                <p className="font-serif text-2xl font-semibold text-[#1B2A4A]">
                  {t.about.name}
                </p>
                <p className="text-sm text-[#475569] mt-1">{t.about.role}</p>
              </div>
            </div>
          </motion.div>

          {/* Right – Real profile photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
            data-testid="about-photo"
            className="relative"
          >
            <div className="relative aspect-[4/5] max-w-md mx-auto lg:ml-auto rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(27,42,74,0.35)]">
              {/* Photo */}
              <img
                src={PROFILE_PHOTO}
                alt={t.about.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />

              {/* Soft inner border for elegance */}
              <div className="absolute inset-4 border border-white/15 rounded-2xl pointer-events-none" />

              {/* Subtle bottom gradient for legibility */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#1B2A4A]/80 via-[#1B2A4A]/30 to-transparent" />

              {/* Corner labels */}
              <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20">
                <div className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />
                <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/90">
                  {t.about.established}
                </span>
              </div>

              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <p className="font-serif text-white text-lg md:text-xl">
                    {t.about.name}
                  </p>
                  <p className="text-white/70 text-[10px] md:text-xs uppercase tracking-[0.2em] mt-1">
                    {t.about.photoCaption}
                  </p>
                </div>
                <span className="text-white/50 text-xs">01 / 01</span>
              </div>
            </div>

            {/* Floating accent badge */}
            <div className="absolute -bottom-8 -right-6 hidden lg:block bg-white border border-[#1B2A4A]/10 rounded-2xl px-6 py-4 shadow-[0_20px_40px_-15px_rgba(27,42,74,0.15)]">
              <p className="font-serif text-2xl font-bold text-[#1B2A4A]">
                {t.about.badgeTitle}
              </p>
              <p className="text-xs text-[#475569] mt-1 uppercase tracking-wider">
                {t.about.badgeSubtitle}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
